import React, { useEffect, useContext, useState } from "react";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { UserContext } from "../../../../context/userContext";
import useInput from "../../../hooks/useInput";
import { getAllCookies, setCookie, removeCookie } from "../../../utils/cookies";
import { Button, Details, Table } from "../../../components";
import styled, { css } from "styled-components";

const PianoTabStyled = styled.div.attrs({
  className: "tab__piano",
})`
  ${(props) => {
    const { theme = {}, darkMode = false } = props;
    const { colors = {}, fonts = {}, media = {} } = theme;
    return css``;
  }}
`;

const PianoTab = () => {
  // eslint-disable-next-line no-unused-vars
  const { experience, ...rest } = useContext(UserContext);
  const {
    value: pianoCookie,
    setValue: setPianoCookie,
    reset: resetPianoCookie,
  } = useInput(null);
  const [executedExperiences, setExecutedExperiences] = useState([]);


  console.log('%c experience ', 'background: green; color: white; display: block;', experience);

  /**----------------------------------
   * METERS
   ---------------------------------- */
  const handleMeterLog = (message, index) => {
    // METER - INVOKED
    if (message.includes("Invoking pageViewMeter")) {
      const regex =
        /Invoking pageViewMeter(.*?) with.* meterName:LITERAL = (.*?),/gm;
      let matched;
      if ((matched = regex.exec(message)) !== null) {
        return {
          meterId: matched[1],
          meterName: matched[2],
        };
      }
    }

    // METER - NOT INVOKED
    if (message.includes("Operations [pageViewMeter")) {
      const regex = /Operations \[pageViewMeter(.*?)] won't be invoked/gm;
      let matched;
      if ((matched = regex.exec(message)) !== null) {
        const messageParts = message.split(",");

        return {
          meterId: matched[1],
          meterSuccess: false,
          meterMessage: messageParts[1],
        };
      }
    }

    if (message.includes("Total page view count")) {
      const regex = /Total page view count = (.*)/gm;
      let matched;
      if ((matched = regex.exec(message)) !== null) {
        return { totalPageViewCount: matched[1] };
      }
    }

    if (message.includes("Is page view meter expired:")) {
      const regex =
        /views before = (.*?)\, current views = (.*?)\, expire at = (.*?)\, should meter be incremented = (.*?)\. Is page view meter expired: (.*)/gm;
      let matched;
      if ((matched = regex.exec(message)) !== null) {
        return {
          viewsBefore: matched[1],
          currentViews: matched[2],
          expireAt: matched[3],
          shouldIncrement: matched[4],
          isExpired: matched[5],
        };
      }
    }

    return undefined;
  };

  /**----------------------------------
   * USER SEGMENTATION
   ---------------------------------- */
  const handleUserSegmentLog = (message, index) => {
    if (message.includes("User segments list [")) {
      const regex = /User segments list \[(.*?)\]/gm;
      let matched;
      if ((matched = regex.exec(message)) !== null) {
        const {
          [0]: type,
          [1]: identifier,
          [2]: value,
        } = matched[1].split(":");

        return {
          type,
          identifier,
          value,
        };
      }
    }

    if (message.includes("has been successfully executed")) {
      const regex = /Operation userSegment.*?has been successfully executed/gm;
      let matched;
      if ((matched = regex.exec(message)) !== null) {
        return {
          result: "true",
        };
      }
    }

    return undefined;
  };

  /**----------------------------------
   * PAGE SEGMENTATION
   ---------------------------------- */
  const handlePageSegmentLog = (message, index) => {
    if (message.includes("Effective pages result: ")) {
      const messageParts = message.split(": ");

      return {
        result: messageParts[1],
      };
    }

    if (message.includes("Target all pages: ")) {
      const messageParts = message.split(": ");

      return {
        targetAllPages: messageParts[1],
      };
    }

    return undefined;
  };

  const startLogRunner = () => {
    if (Object.keys(experience).length <= 0 || !"result" in experience)
      return [];
    const logMessages = get(experience, "result.debugMessages", []);
    const executingExperiences = get(experience, "result.experiences", []);

    const getExperienceId = (message) => {
      const regex = /Executing EBL with tid `(.*?)`/gm;
      let matched;
      if ((matched = regex.exec(message)) !== null) {
        return matched[1]; // Experience ID
      }
      return "";
    };

    let activeExperience = {
      id: null,
    };
    let activeExperiences = [];

    logMessages.forEach((message, index) => {
      // EXPERIENCE INIT
      if (message.includes("Executing EBL with tid")) {
        activeExperience.id = getExperienceId(message);

        const matchingExperience = executingExperiences.find(
          (experience) => experience.id === activeExperience.id
        );
        if (matchingExperience) {
          activeExperience.schedule = matchingExperience.schedule;
          activeExperience.status = matchingExperience.status;
          activeExperience.title = matchingExperience.title;
          activeExperience.type = matchingExperience.type;
          activeExperience.updateDate = matchingExperience.updateDate;
          activeExperience.version = matchingExperience.version;
        }
        return;
      }

      // HANDLE METERS
      let meterData = handleMeterLog(message, index);
      if (meterData) {
        activeExperience.isMetered = true;
        activeExperience.meterData = {
          ...activeExperience.meterData,
          ...meterData,
        };
      }

      // HANDLE USER SEGMENTATION
      let userSegmentData = handleUserSegmentLog(message, index);
      if (userSegmentData) {
        activeExperience.userSegmentData = {
          ...activeExperience.userSegmentData,
          ...userSegmentData,
        };
      }

      // HANDLE PAGE SEGMENTATION
      let pageSegmentData = handlePageSegmentLog(message, index);
      if (pageSegmentData) {
        activeExperience.pageSegmentData = {
          ...activeExperience.pageSegmentData,
          ...pageSegmentData,
        };
      }

      // EXPERIENCE COMPLETE
      if (message.includes("All routes have been evaluated")) {
        activeExperiences.push(activeExperience);
        activeExperience = {};
      }
    });

    setExecutedExperiences(activeExperiences);
  };

  // Check for existing Piano custom cookies w/ prefix `__pc__`
  const getExistingCustomCookies = () => {
    const allCookies = getAllCookies();
    // const allCookies = {};
    const customPianoCookies = Object.keys(allCookies).filter((cookie) =>
      cookie.startsWith("_pc_")
    );
    const customPianoCookieArray = customPianoCookies.map((cookie) => ({
      name: cookie,
      value: allCookies[cookie],
    }));
    return customPianoCookieArray;
  };

  // Transform the key/value pairs of an objet into a table for display
  const tableizeObject = (object, name = "Data", open = false) => {
    return (
      <Details open={open} summary={name}>
        <Table className="mt-md mb-md">
          <tr>
            {Object.keys(object).map((item, i) => (
              <th key={`debug-table-header-${i}`}>{item}</th>
            ))}
          </tr>
          <tr>
            {Object.values(object).map((item, i) => {
              let value = item;
              if (typeof item === "boolean") {
                value = JSON.stringify(item);
              }
              if (typeof item === "object" && item) {
                value = tableizeObject(item);
              }
              return <td key={`debug-table-data-${i}`}>{value}</td>;
            })}
          </tr>
        </Table>
      </Details>
    );
  };

  useEffect(() => {
    // Execute the log runner
    startLogRunner();
  }, [experience]);

  const existingCustomCookies = getExistingCustomCookies();

  console.log(
    "%c 🎹 final experience result ",
    "background: orange; color: black; display: block;",
    executedExperiences
  );

  return (
    <PianoTabStyled>
      {/* EXPERIENCE DISPLAY */}
      <Details open summary="Experiences">
        <div className={`piano__container`}>
          {executedExperiences.map((data, i) => {
            let dataValues = Object.values(executedExperiences)[i];
            if (isEmpty(dataValues)) return null; // If empty object, return nothing
            return (
              <div key={`data-object-${i}`} className={css.tableDataContainer}>
                {tableizeObject(
                  dataValues,
                  `${data.id} - ${data.title} | ${
                    data.pageSegmentData.result === "true" &&
                    data.userSegmentData.result === "true"
                      ? "✅"
                      : "❌"
                  } | ${data.isMetered ? "🌡️" : ""}`
                )}
              </div>
            );
          })}
        </div>
      </Details>

      {/* COOKIE UTILS  */}
      <Details summary="Cookie Utilities">
        <div className="pianoFormGroup__cookies">
          <label>Piano Custom Cookie</label>
          <br />
          <input
            type="text"
            placeholder="Cookie name"
            value={pianoCookie}
            onChange={(e) => {
              setPianoCookie(e.target.value);
            }}
          />
          <Button
            onClick={() => {
              setCookie(`_pc_${pianoCookie}`, "true");
              resetPianoCookie(); // reset input
              window.location.reload();
            }}
          >
            SET
          </Button>
        </div>

        <div>
          {["bottom", "inline", "flyin"].map((cookieName) => (
            <Button
              onClick={() => {
                setCookie(`_pc_${cookieName}`, "true");
                window.location.reload();
              }}
            >
              {`Set ${cookieName} cookie`}
            </Button>
          ))}
        </div>

        {/* EXISTING CUSTOM USER COOKIES  */}
        {existingCustomCookies.length > 0 && (
          <Details summary="Custom Cookies">
            <Table className="mt-md mb-md">
              <tr>
                <th>Name</th>
                <th>Value</th>
                <th>Remove?</th>
              </tr>
              {existingCustomCookies.map((cookie) => (
                <tr>
                  <td>{cookie.name}:</td>
                  <td>{cookie.value}</td>
                  <td>
                    <div
                      style={{
                        cursor: "pointer",
                        textAlign: "center",
                        color: "red",
                      }}
                      onClick={() => {
                        removeCookie(cookie.name);
                        window.location.reload();
                      }}
                    >
                      X
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          </Details>
        )}
      </Details>
    </PianoTabStyled>
  );
};

export default PianoTab;
